/**
 * nativeCache — instant-paint helpers layered on top of swrCache.
 *
 * Why: even with the SWR axios adapter, a network profile still (a) renders
 * a skeleton on the first paint, (b) waits ~100-300ms for hero images to
 * appear on cellular, and (c) fires a fresh HTTP round-trip the *moment*
 * the user taps a name — the fetch only starts AFTER navigation. This
 * module removes those three hits:
 *
 *   peekCache(path)   – synchronous localStorage read so the page can seed
 *                       its useState with cached data BEFORE the first
 *                       paint. On repeat visits the skeleton is skipped
 *                       entirely; SWR still revalidates in the background.
 *
 *   prefetchApi(path) – fire a background GET so the response lands in the
 *                       SWR cache before the user routes to the target
 *                       page. Idempotent — safe to call from onTouchStart.
 *
 *   warmImages(urls)  – preload hero / related images off the main thread.
 *                       new Image() is enough: WKWebView caches decoded
 *                       bitmaps once the fetch resolves.
 *
 *   preheatProfile(slug) – one-liner that prefetches the profile aggregator
 *                          endpoint AND warms its hero image the moment the
 *                          profile response comes back.
 *
 *   linkHoverProps(slug) – spread into <Link> to auto-prefetch on hover /
 *                          touchstart. Zero-cost on the initial render.
 *
 * All cache reads/writes route through the same `flyt:swr:` localStorage
 * namespace as swrCache so a hit from either code path warms the other.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_PREFIX = 'flyt:swr:';
const IMG_KEY = 'flyt:img-warmed';

// In-memory sets — dedupe repeated prefetches within the same session.
const _apiInflight = new Set();
const _apiDone = new Set();
const _imgWarmed = new Set();

/**
 * Read a cached response synchronously. Path should be exactly the
 * `pathname + search` that swrCache stored (e.g. `/api/network/athletes/foo/profile`).
 */
export function peekCache(pathAndQuery) {
  if (!pathAndQuery || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + pathAndQuery);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.d ?? null;
  } catch {
    return null;
  }
}

/** Fire-and-forget GET. The SWR axios adapter will fill localStorage. */
export function prefetchApi(pathAndQuery) {
  if (!pathAndQuery || _apiDone.has(pathAndQuery) || _apiInflight.has(pathAndQuery)) return;
  _apiInflight.add(pathAndQuery);
  const url = pathAndQuery.startsWith('http') ? pathAndQuery : `${process.env.REACT_APP_BACKEND_URL}${pathAndQuery}`;
  axios.get(url)
    .then(() => { _apiDone.add(pathAndQuery); })
    .catch(() => { /* offline — retry allowed on next hover */ })
    .finally(() => { _apiInflight.delete(pathAndQuery); });
}

/**
 * Warm one or more image URLs. Uses new Image() which triggers the WKWebView
 * HTTP cache without occupying an <img> element. Silent on failure.
 */
export function warmImages(urls) {
  if (!urls || typeof window === 'undefined') return;
  const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  for (const u of list) {
    if (!u || _imgWarmed.has(u)) continue;
    _imgWarmed.add(u);
    try {
      const img = new Image();
      // decoding=async lets the WebView schedule decode off the main thread.
      img.decoding = 'async';
      img.loading = 'eager';
      img.src = u;
    } catch { /* ignore */ }
  }
}

/**
 * Preheat everything the /network/{slug} page needs:
 *   – the profile aggregator endpoint (single call — carries stories,
 *     shorts, live_game, hero_story)
 *   – the hero image the moment the profile object is known (cached in
 *     localStorage from a previous visit) or once the fetch resolves.
 *
 * Idempotent + cheap; safe to call on hover / touchstart / list render.
 */
export function preheatProfile(slug) {
  if (!slug) return;
  const path = `/api/network/athletes/${slug}/profile`;

  // If we already have a cached snapshot, warm its images immediately.
  const cached = peekCache(path);
  if (cached?.athlete) {
    const heroes = [
      cached.athlete.network_hero_image,
      cached.athlete.network_card_image,
      cached.athlete.photo,
      cached.athlete.flyt_link_photo,
    ].filter(Boolean).map((u) => resolveWebpUrl(u, 1200));
    warmImages(heroes);
  }

  // Fire the network warm regardless — SWR will noop if it's already fresh.
  prefetchApi(path);
}

/**
 * onMouseEnter / onTouchStart prefetch spread for <Link to="/network/{slug}">.
 * Usage: <Link to={`/network/${a.slug}`} {...linkHoverProps(a.slug)} />
 */
export function linkHoverProps(slug) {
  if (!slug) return {};
  const fire = () => preheatProfile(slug);
  return { onMouseEnter: fire, onTouchStart: fire, onFocus: fire };
}

/**
 * Persist a snapshot of hero URLs so the very first paint on next launch
 * of the app can insert a `<link rel="preload">` in index.html without a
 * network round-trip. Called by AthleteNetworkPage once the profile loads.
 */
export function rememberHeroForSlug(slug, heroUrl) {
  if (!slug || !heroUrl || typeof window === 'undefined') return;
  try {
    const raw = window.localStorage.getItem(IMG_KEY) || '{}';
    const obj = JSON.parse(raw);
    obj[slug] = { u: heroUrl, t: Date.now() };
    // keep at most 40 slugs
    const keys = Object.keys(obj).sort((a, b) => (obj[b].t || 0) - (obj[a].t || 0));
    if (keys.length > 40) {
      keys.slice(40).forEach((k) => delete obj[k]);
    }
    window.localStorage.setItem(IMG_KEY, JSON.stringify(obj));
  } catch { /* ignore quota */ }
}

/** Quick lookup for the last hero image URL for a slug (nullable). */
export function lastHeroForSlug(slug) {
  if (!slug || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(IMG_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    return obj[slug]?.u || null;
  } catch { return null; }
}

// Small helper — replicates the resolveImageUrl WebP rewrite without
// importing ImageUpload.js (which would drag react into the tree).
function resolveWebpUrl(url, width = 1200) {
  if (!url) return '';
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  const backend = process.env.REACT_APP_BACKEND_URL || '';
  const abs = (url.startsWith('http://') || url.startsWith('https://')) ? url : `${backend}${url}`;
  try {
    const u = new URL(abs);
    if (u.hostname === 'images.unsplash.com') {
      u.searchParams.set('w', String(width));
      u.searchParams.set('auto', 'format');
      return u.toString();
    }
    if (u.pathname.startsWith('/api/img')) return abs;
    // Route uploads through the WebP fast-lane
    if (/\/api\/(?:files|uploads)\//i.test(abs)) {
      return `${backend}/api/img?u=${encodeURIComponent(abs)}&w=${width}`;
    }
  } catch { /* fallthrough */ }
  return abs;
}

// Re-export API root so callers can compose paths without duplicating env logic.
export { API };
